/**
 * Track123 loader — resilient mount with loading/error states.
 * Retries when the app stays on its internal loading spinner.
 * Also patches Track123 mixin CSS bugs:
 * - middleScreen CSS requested as /build/middleScreen-tradition.min.css?v=VER (404)
 *   must be /build/VER/middleScreen-tradition.min.css
 * - relative `undefined/tracking-page/...` → /pages/undefined/...
 */
(function () {
  'use strict';

  var SRC = 'https://shp.track123.com/tracking-page/build/widget.min.js';
  var BASE = 'https://shp.track123.com';
  var VERSION_URL = BASE + '/tracking-page/version.json';
  var READY_MS = 18000;
  var POLL_MS = 400;
  var cssInterceptInstalled = false;
  var middleScreenPreloaded = false;

  // Vendor mixins read these globals when injecting responsive CSS.
  try {
    if (!window.track123BaseUrl) window.track123BaseUrl = BASE;
  } catch (e) {}

  function rootEl() {
    return document.getElementById('gds-track123-widget');
  }

  function fixMiddleScreenCssHref(href) {
    if (!href || href.indexOf('middleScreen-tradition') === -1) return null;
    // Already versioned path — leave alone.
    if (/\/build\/\d+\/middleScreen-tradition\.min\.css/.test(href)) return null;
    var version = window.track123Version || '';
    var m = String(href).match(/[?&]v=([^&]+)/);
    if (m) version = m[1];
    if (!version) {
      m = String(href).match(/\/build\/(\d+)\//);
      if (m) version = m[1];
    }
    if (!version) return null;
    return BASE + '/tracking-page/build/' + version + '/middleScreen-tradition.min.css';
  }

  function patchStylesheetLinks(root) {
    var scope = root || document;
    var links = scope.querySelectorAll
      ? scope.querySelectorAll('link[rel="stylesheet"]')
      : [];
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var href = link.getAttribute('href') || link.href || '';
      var fixed = fixMiddleScreenCssHref(href);
      if (fixed && href !== fixed) link.href = fixed;
    }
  }

  function preloadMiddleScreenCss() {
    var version = window.track123Version;
    if (!version || middleScreenPreloaded) return;
    var href = BASE + '/tracking-page/build/' + version + '/middleScreen-tradition.min.css';
    if (document.querySelector('link[data-gds-track123-midscreen]')) {
      middleScreenPreloaded = true;
      return;
    }
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-gds-track123-midscreen', '1');
    document.head.appendChild(link);
    middleScreenPreloaded = true;
  }

  function installCssIntercept() {
    if (cssInterceptInstalled) return;
    cssInterceptInstalled = true;
    try {
      var desc = Object.getOwnPropertyDescriptor(HTMLLinkElement.prototype, 'href');
      if (desc && desc.set) {
        var originalSet = desc.set;
        Object.defineProperty(HTMLLinkElement.prototype, 'href', {
          configurable: true,
          enumerable: desc.enumerable,
          get: desc.get,
          set: function (value) {
            var fixed = fixMiddleScreenCssHref(String(value || ''));
            originalSet.call(this, fixed || value);
          }
        });
      }
      var originalSetAttribute = HTMLLinkElement.prototype.setAttribute;
      HTMLLinkElement.prototype.setAttribute = function (name, value) {
        if (String(name).toLowerCase() === 'href') {
          var fixed = fixMiddleScreenCssHref(String(value || ''));
          if (fixed) value = fixed;
        }
        return originalSetAttribute.call(this, name, value);
      };
    } catch (e) {}
  }

  function watchBrokenCss() {
    installCssIntercept();
    patchStylesheetLinks(document);
    preloadMiddleScreenCss();
    try {
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes || [];
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (!node || node.nodeType !== 1) continue;
            if (node.tagName === 'LINK') patchStylesheetLinks(document);
            else if (node.querySelectorAll) patchStylesheetLinks(node);
          }
        }
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  function ensureVersion(cb) {
    if (window.track123Version) {
      cb(null, window.track123Version);
      return;
    }
    try {
      fetch(VERSION_URL, { credentials: 'omit', mode: 'cors' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && data.version) {
            try { window.track123Version = String(data.version); } catch (e) {}
          }
          cb(null, window.track123Version || null);
        })
        .catch(function () { cb(null, null); });
    } catch (e) {
      cb(null, null);
    }
  }

  function setStatus(root, kind, message) {
    if (!root) return;
    var existing = root.querySelector('[data-gds-track-status]');
    if (existing) existing.remove();
    if (!message) return;
    var el = document.createElement('p');
    el.setAttribute('data-gds-track-status', kind);
    el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    el.className = 'gds-track-status gds-track-status--' + kind;
    el.textContent = message;
    root.insertBefore(el, root.firstChild);
  }

  function isReady(root) {
    if (!root) return false;
    if (root.querySelector('input')) return true;
    if (root.querySelector('button')) return true;
    if (root.querySelector('.track123_track_wrapper')) return true;
    if (root.querySelector('.track123_tab_form_background')) return true;
    var text = (root.innerText || '').trim();
    return /order number|tracking number|track your order/i.test(text);
  }

  function isStuckLoading(root) {
    if (!root) return false;
    if (isReady(root)) return false;
    return !!root.querySelector('.track123_loading') || !(root.querySelector('track123-tracking-widget'));
  }

  function mountWidget(root) {
    if (!root) return null;
    var existing = root.querySelector('track123-tracking-widget');
    if (existing) return existing;

    var el = document.createElement('track123-tracking-widget');
    var domain = root.getAttribute('data-shopify-domain') || '';
    var locale = root.getAttribute('data-locale') || 'en';
    if (domain) el.setAttribute('shopifyDomain', domain);
    if (locale) el.setAttribute('locale', locale);
    root.appendChild(el);
    return el;
  }

  function remount(root) {
    if (!root) return;
    var old = root.querySelector('track123-tracking-widget');
    if (old) old.remove();
    mountWidget(root);
  }

  function watchReady(root, attempt) {
    var started = Date.now();
    setStatus(root, 'loading', 'Loading tracking form…');

    var observer = null;
    try {
      observer = new MutationObserver(function () {
        if (isReady(root)) {
          setStatus(root, '', '');
          if (observer) observer.disconnect();
        }
      });
      observer.observe(root, { childList: true, subtree: true, characterData: true });
    } catch (e) {}

    var timer = setInterval(function () {
      if (isReady(root)) {
        clearInterval(timer);
        if (observer) observer.disconnect();
        setStatus(root, '', '');
        return;
      }
      if (Date.now() - started < READY_MS) return;
      clearInterval(timer);
      if (observer) observer.disconnect();

      if (attempt < 1) {
        remount(root);
        watchReady(root, attempt + 1);
        return;
      }

      setStatus(
        root,
        'error',
        'Tracking is taking longer than expected. Please refresh this page, or contact support with your order number.'
      );
    }, POLL_MS);
  }

  function loadScript(cb) {
    if (window.customElements && customElements.get('track123-tracking-widget')) {
      cb(null);
      return;
    }
    var prev = document.querySelector('script[data-gds-track123]');
    if (prev) {
      prev.addEventListener('load', function () { cb(null); });
      prev.addEventListener('error', function () { cb(new Error('script')); });
      return;
    }
    var s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.setAttribute('data-gds-track123', '1');
    s.onload = function () { cb(null); };
    s.onerror = function () { cb(new Error('script')); };
    document.head.appendChild(s);
  }

  function start() {
    var root = rootEl();
    if (!root) return;

    installCssIntercept();

    ensureVersion(function () {
      watchBrokenCss();

      loadScript(function (err) {
        if (err) {
          setStatus(
            root,
            'error',
            'Tracking is temporarily unavailable. Please refresh and try again, or contact support.'
          );
          return;
        }
        try {
          if (!window.track123BaseUrl) window.track123BaseUrl = BASE;
        } catch (e) {}
        mountWidget(root);
        watchReady(root, 0);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
